import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown,
  Plus,
  Building2,
  DollarSign,
  BarChart3,
  Calendar,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Bell,
  Eye,
  EyeOff,
  ShoppingCart,
  Banknote
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Area, 
  AreaChart,
  Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StockDetail() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [stock, setStock] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState(null);
  const [dataInfo, setDataInfo] = useState(null);
  const [projection, setProjection] = useState(null);
  const [indexHistory, setIndexHistory] = useState([]);
  const [period, setPeriod] = useState('1M');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chart');
  const [watchStatus, setWatchStatus] = useState(null); // null, 'watch', 'interested_buy', 'interested_sell'

  const periods = [
    { value: '1W', label: '1W' },
    { value: '1M', label: '1M' },
    { value: '3M', label: '3M' },
    { value: '6M', label: '6M' },
    { value: '1Y', label: '1Y' },
    { value: '5Y', label: '5Y' }
  ];

  const fetchStockData = useCallback(async () => {
    try {
      const [stockRes, projectionRes] = await Promise.all([
        axios.get(`${API}/stocks/${symbol}`),
        axios.get(`${API}/stocks/${symbol}/projection?years=5`)
      ]);
      setStock(stockRes.data);
      setProjection(projectionRes.data);
    } catch (error) {
      console.error('Failed to fetch stock:', error);
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  const fetchHistory = useCallback(async () => {
    try {
      const [historyRes, indexRes] = await Promise.all([
        axios.get(`${API}/stocks/${symbol}/history?period=${period}`),
        axios.get(`${API}/market/index/DSEI/history?period=${period}`)
      ]);
      setHistory(historyRes.data.history);
      setHistoryStats(historyRes.data.statistics);
      setDataInfo(historyRes.data.data_info);
      setIndexHistory(indexRes.data.history);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  }, [symbol, period]);

  useEffect(() => {
    fetchStockData();
  }, [fetchStockData]);

  useEffect(() => {
    if (stock) {
      fetchHistory();
    }
  }, [stock, fetchHistory]);

  // Fetch user's watchlist status for this stock
  useEffect(() => {
    const fetchWatchStatus = async () => {
      if (!token || !symbol) return;
      try {
        const response = await axios.get(`${API}/watchlist`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const entry = response.data.watchlist.find(w => w.symbol === symbol);
        if (entry) {
          setWatchStatus(entry.action);
        }
      } catch (error) {
        console.error('Failed to fetch watchlist:', error);
      }
    };
    fetchWatchStatus();
  }, [token, symbol]);

  const updateWatchStatus = async (action) => {
    try {
      await axios.post(`${API}/watchlist`, 
        { symbol, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (action === 'unwatch') {
        setWatchStatus(null);
        toast.success(t('Removed from watchlist', 'Imeondolewa kwenye orodha'));
      } else {
        setWatchStatus(action);
        const messages = {
          watch: t('Added to watchlist', 'Imeongezwa kwenye orodha'),
          interested_buy: t('Marked as interested to buy', 'Imewekwa alama ya kupenda kununua'),
          interested_sell: t('Marked as interested to sell', 'Imewekwa alama ya kupenda kuuza')
        };
        toast.success(messages[action]);
      }
    } catch (error) {
      toast.error(t('Failed to update', 'Imeshindwa kusasisha'));
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000000000) {
      return `${(amount / 1000000000000).toFixed(2)}T`;
    } else if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(2)}B`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    }
    return amount.toLocaleString();
  };

  // Normalize data for comparison chart
  const getComparisonData = () => {
    if (!history.length || !indexHistory.length) return [];
    
    const stockBase = history[0]?.close || 1;
    const indexBase = indexHistory[0]?.close || 1;
    
    return history.map((item, i) => ({
      date: item.date,
      stock: ((item.close / stockBase) * 100).toFixed(2),
      index: indexHistory[i] ? ((indexHistory[i].close / indexBase) * 100).toFixed(2) : 100
    }));
  };

  // Format projection data for chart
  const getProjectionChartData = () => {
    if (!projection || !projection.projections) return [];
    
    // Start with current price
    const data = [{ 
      year: 'Now', 
      conservative: stock?.current_price, 
      base: stock?.current_price, 
      optimistic: stock?.current_price 
    }];
    
    // Add projection data - backend returns array of {year, conservative, expected, optimistic}
    projection.projections.forEach(p => {
      data.push({
        year: `Y${p.year}`,
        conservative: p.conservative,
        base: p.expected,
        optimistic: p.optimistic
      });
    });
    
    return data;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!stock) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Stock not found</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Go Back
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-enter space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('Back', 'Rudi')}
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <span className="font-bold text-primary text-lg">{stock.symbol}</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{stock.name}</h1>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{language === 'sw' ? stock.sector_sw : stock.sector}</span>
                  {stock.cross_listed && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                      Cross-Listed
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={() => navigate(`/portfolio/add?symbol=${stock.symbol}`)}
              className="rounded-full"
              data-testid="add-to-portfolio-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('Add to Portfolio', 'Ongeza Portfolio')}
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate(`/alerts?symbol=${stock.symbol}`)}
              className="rounded-full"
              data-testid="set-alert-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              {t('Set Alert', 'Weka Tahadhari')}
            </Button>
          </div>
        </div>

        {/* Sentiment/Watchlist Buttons */}
        <div className="flex flex-wrap gap-2 p-3 bg-muted/50 rounded-lg">
          <span className="text-sm text-muted-foreground self-center mr-2">
            {t('Track this stock:', 'Fuatilia hisa hii:')}
          </span>
          <Button
            variant={watchStatus === 'watch' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateWatchStatus(watchStatus === 'watch' ? 'unwatch' : 'watch')}
            data-testid="watch-btn"
          >
            {watchStatus === 'watch' ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {watchStatus === 'watch' ? t('Watching', 'Inafuatiliwa') : t('Watch', 'Fuatilia')}
          </Button>
          <Button
            variant={watchStatus === 'interested_buy' ? 'default' : 'outline'}
            size="sm"
            className={watchStatus === 'interested_buy' ? 'bg-green-600 hover:bg-green-700' : ''}
            onClick={() => updateWatchStatus(watchStatus === 'interested_buy' ? 'unwatch' : 'interested_buy')}
            data-testid="interested-buy-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-1" />
            {t('Want to Buy', 'Nataka Kununua')}
          </Button>
          <Button
            variant={watchStatus === 'interested_sell' ? 'default' : 'outline'}
            size="sm"
            className={watchStatus === 'interested_sell' ? 'bg-red-600 hover:bg-red-700' : ''}
            onClick={() => updateWatchStatus(watchStatus === 'interested_sell' ? 'unwatch' : 'interested_sell')}
            data-testid="interested-sell-btn"
          >
            <Banknote className="w-4 h-4 mr-1" />
            {t('Want to Sell', 'Nataka Kuuza')}
          </Button>
        </div>

        {/* Price Card */}
        <Card className="border-border/50 overflow-hidden">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  {t('Current Price', 'Bei ya Sasa')}
                </p>
                <p className="text-3xl font-bold text-foreground">
                  TZS {stock.current_price.toLocaleString()}
                </p>
                <div className={`flex items-center gap-1 mt-1 ${
                  stock.ytd_change >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stock.ytd_change >= 0 ? (
                    <ArrowUpRight className="w-4 h-4" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4" />
                  )}
                  <span className="font-medium">
                    {stock.ytd_change >= 0 ? '+' : ''}{stock.ytd_change.toFixed(2)}% YTD
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  {t('Market Cap', 'Thamani ya Soko')}
                </p>
                <p className="text-xl font-semibold text-foreground">
                  TZS {formatCurrency(stock.market_cap)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  {t('52W Range', 'Kiwango cha 52W')}
                </p>
                <p className="text-lg font-medium text-foreground">
                  {stock.year_low.toLocaleString()} - {stock.year_high.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  {t('P/E Ratio', 'Uwiano P/E')}
                </p>
                <p className="text-xl font-semibold text-foreground">
                  {stock.pe_ratio.toFixed(1)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
            <TabsTrigger value="chart" data-testid="tab-chart">
              {t('Price Chart', 'Chati ya Bei')}
            </TabsTrigger>
            <TabsTrigger value="compare" data-testid="tab-compare">
              {t('vs Index', 'dhidi ya Index')}
            </TabsTrigger>
            <TabsTrigger value="projection" data-testid="tab-projection">
              {t('Projection', 'Makadirio')}
            </TabsTrigger>
          </TabsList>

          {/* Period Selector */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4 mb-2">
            <div className="flex gap-2 flex-wrap">
              {periods.map(p => (
                <Button
                  key={p.value}
                  variant={period === p.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPeriod(p.value)}
                  className="rounded-full"
                  data-testid={`period-${p.value}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            {/* Data Source Indicator */}
            {dataInfo && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full">
                <div className={`w-2 h-2 rounded-full ${
                  dataInfo.source_type === 'live' ? 'bg-emerald-500 animate-pulse' :
                  dataInfo.source === 'database' ? 'bg-emerald-500' : 'bg-amber-500'
                }`} />
                <span>
                  {language === 'sw' ? dataInfo.source_label_sw : dataInfo.source_label}
                  {dataInfo.source_type === 'live' && (
                    <span className="ml-1 text-emerald-600 font-medium">● LIVE</span>
                  )}
                  {' • '}{dataInfo.data_points} {t('points', 'pointi')}
                  {dataInfo.last_updated && (
                    <> • {t('Updated', 'Imesasishwa')} {new Date(dataInfo.last_updated).toLocaleDateString()}</>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Price Chart Tab */}
          <TabsContent value="chart" className="mt-4">
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                {historyStats && (
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t('Period Change', 'Mabadiliko')}:</span>
                      <span className={`ml-1 font-semibold ${historyStats.period_change_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {historyStats.period_change_percent >= 0 ? '+' : ''}{historyStats.period_change_percent.toFixed(2)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('High', 'Juu')}:</span>
                      <span className="ml-1 font-semibold">TZS {historyStats.period_high?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('Low', 'Chini')}:</span>
                      <span className="ml-1 font-semibold">TZS {historyStats.period_low?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('Avg Volume', 'Wastani Kiasi')}:</span>
                      <span className="ml-1 font-semibold">{historyStats.average_volume?.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={history}>
                      <defs>
                        <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return period === '5Y' ? date.getFullYear() : 
                                 period === '1Y' ? date.toLocaleDateString('en', { month: 'short' }) :
                                 date.toLocaleDateString('en', { day: 'numeric', month: 'short' });
                        }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => value.toLocaleString()}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`TZS ${Number(value).toLocaleString()}`, 'Price']}
                        labelFormatter={(label) => new Date(label).toLocaleDateString()}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="close" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        fill="url(#colorPrice)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comparison Tab */}
          <TabsContent value="compare" className="mt-4">
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {stock.symbol} vs DSE Index ({t('Normalized to 100', 'Imesawazishwa hadi 100')})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={getComparisonData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return date.toLocaleDateString('en', { day: 'numeric', month: 'short' });
                        }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                        domain={['auto', 'auto']}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="stock" 
                        name={stock.symbol}
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="index" 
                        name="DSEI"
                        stroke="hsl(var(--accent))" 
                        strokeWidth={2}
                        dot={false}
                        strokeDasharray="5 5"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projection Tab */}
          <TabsContent value="projection" className="mt-4">
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {t('5-Year Price Projection', 'Makadirio ya Bei ya Miaka 5')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={getProjectionChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => value === 0 ? t('Now', 'Sasa') : `${t('Year', 'Mwaka')} ${value}`}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => formatCurrency(value)}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`TZS ${Number(value).toLocaleString()}`, '']}
                        labelFormatter={(value) => value === 0 ? t('Current', 'Sasa') : `${t('Year', 'Mwaka')} ${value}`}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="optimistic" 
                        name={t('Optimistic', 'Matumaini')}
                        stroke="#22c55e" 
                        strokeWidth={2}
                        dot
                      />
                      <Line 
                        type="monotone" 
                        dataKey="base" 
                        name={t('Base Case', 'Hali ya Msingi')}
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        dot
                      />
                      <Line 
                        type="monotone" 
                        dataKey="conservative" 
                        name={t('Conservative', 'Mhafidhina')}
                        stroke="hsl(var(--accent))" 
                        strokeWidth={2}
                        dot
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  {t(
                    'Projections are based on historical performance and are for educational purposes only. Actual results may vary.',
                    'Makadirio yanategemea utendaji wa kihistoria na ni kwa madhumuni ya elimu tu. Matokeo halisi yanaweza kutofautiana.'
                  )}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Stock Info */}
        <Card className="border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {t('About', 'Kuhusu')} {stock.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              {language === 'sw' ? stock.description_sw : stock.description_en}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">{t('Sector', 'Sekta')}</p>
                <p className="font-medium text-foreground">
                  {language === 'sw' ? stock.sector_sw : stock.sector}
                </p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">{t('P/E Ratio', 'Uwiano P/E')}</p>
                <p className="font-medium text-foreground">{stock.pe_ratio.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">{t('Dividend Yield', 'Mavuno ya Gawio')}</p>
                <p className="font-medium text-foreground">{stock.dividend_yield.toFixed(2)}%</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">{t('Currency', 'Sarafu')}</p>
                <p className="font-medium text-foreground">{stock.currency}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
